import _ from 'lodash'
import { fileTypeFromBuffer } from 'file-type'
import { RemoteFile } from 'shared-libs/generated/server-types/entity/commentEntity'
import { Logger } from 'browser/apis/logging'
import { getFileExtension } from 'shared-libs/helpers/utils'

const IOS_REGEXES = [
  /(iPhone|iPad|iOS)/
]

export function isZoomable(image, padding, containerWidth, containerHeight) {
  const imageWidth = image.width
  const imageHeight = image.height
  const adjContainerWidth = containerWidth - padding * 2
  const adjContainerHeight = containerHeight - padding * 2
  return imageWidth > adjContainerWidth || imageHeight > adjContainerHeight
}

export function getMinZoomLevel(image, padding, containerWidth, containerHeight) {
  if (!image || !image.width || !image.height) {
    return 1 // default zoom value
  }

  let imageWidth = image.width
  let imageHeight = image.height
  const aspectRatio = imageHeight / imageWidth
  const adjContainerWidth = containerWidth - padding * 2
  const adjContainerHeight = containerHeight - padding * 2
  if (imageWidth > adjContainerWidth) {
    imageWidth = adjContainerWidth
    imageHeight = imageWidth * aspectRatio
  }
  if (imageHeight > adjContainerHeight) {
    imageHeight = adjContainerHeight
    imageWidth = imageHeight / aspectRatio
  }
  return imageWidth / image.width
}

export function getFitWidthZoomLevel(image, padding, containerWidth) {
  let imageWidth = image.width
  const adjContainerWidth = containerWidth - padding * 2
  imageWidth = adjContainerWidth
  return imageWidth / image.width
}


export function isIOSDevice() {
  const desktopOrIPad = 'MacIntel'
  const userAgent = navigator.userAgent
  for (const regex of IOS_REGEXES) {
    if (userAgent.match(regex)) {
      return true
    }
  }
  // A bit of a hack, platform is deprecated. TODO(Bobbi): find a more reliable test
  return (navigator.platform === desktopOrIPad && navigator.maxTouchPoints > 2)
}

async function getImageFromFile(file): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onerror = (evt) => {
      reject(new Error('failed to load image'))
    }
    img.onload = () => {
      resolve(img)
    }
    img.src = URL.createObjectURL(file)
  })
}


export async function convertCanvasToBlob(
  canvas: HTMLCanvasElement,
  contentType: string,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob)
      },
      contentType,
      quality
    )
  })
}

/**
 * Compress the image using jsquash (libwebp via wasm), falling back to jpeg compression if that
 * fails, e.g. due to OOM issues on wasm heap.
 */
export async function convertToWebp(file: File, quality = 0.8, logCtx?: ImageOptimizationContext): Promise<File> {
  let imageWidth = 0
  let imageHeight = 0
  let canvas: HTMLCanvasElement | null = null
  let ctx: CanvasRenderingContext2D | null = null
  let wasmModule: any = null
  try {
    const { default: encode, init } = await import('@jsquash/webp/encode')
    wasmModule = await init()
    const image = await getImageFromFile(file)
    imageWidth = image.width
    imageHeight = image.height
    canvas = document.createElement('canvas')
    canvas.width = imageWidth
    canvas.height = imageHeight
    ctx = canvas.getContext('2d')
    if (!ctx) {
      console.warn(`Could not getContext('2d') for file ${file.name}`)
      return file
    }
    ctx.drawImage(image, 0, 0)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const buffer = await encode(imageData, { quality: Math.round(quality * 100) })
    const name = changeFileExtension(file.name, 'webp')
    return new File([buffer], name, { type: 'image/webp' })
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e)
    console.error(`Could not convert to webp: ${message}`, e)
    Logger.logEvent('[image optimization] webp conversion failed', {
      message,
      fileType: file.type,
      fileExtension: getFileExtension(file.name),
      fileSize: file.size,
      imageWidth,
      imageHeight,
      // libwebp allocates input pixels plus internal working buffers in the WASM heap, so 1.3-2.5x
      // imageRgbaBytes approaching wasmHeapBytes could suggest possible heap exhaustion.
      imageRgbaBytes: imageWidth * imageHeight * 4,
      wasmHeapBytes: wasmModule?.HEAP8?.buffer?.byteLength,
      ...logCtx,
    })

    if (canvas && ctx) {
      // attempt a browser-native JPEG encode at 70%, off the wasm heap.
      try {
        const blob = await convertCanvasToBlob(canvas, 'image/jpeg', 0.7)
        return new File([blob], changeFileExtension(file.name, 'jpg'), { type: 'image/jpeg' })
      } catch (jpegError) {
        const jpegMessage = jpegError instanceof Error ? jpegError.message : String(jpegError)
        console.error('JPEG fallback also failed', jpegError)
        Logger.logEvent('[image optimization] webp fallback to jpeg failed', {
          message: jpegMessage,
          fileType: file.type,
          fileExtension: getFileExtension(file.name),
          fileSize: file.size,
          imageWidth,
          imageHeight,
          ...logCtx,
        })
      }
    }
    return file
  }
}

export function changeFileExtension(filename: string, ext: string): string {
  const extPattern = /\.[^/.]+$/
  if (extPattern.test(filename)) {
    return filename.replace(extPattern, `.${ext}`)
  } else {
    return `${filename}.${ext}`
  }
}

export async function getFileMimeType(file: File): Promise<string> {
  const headerBlob = file.slice(0, 4100) // minimum bytes
  const reader = new FileReader()
  return new Promise((resolve, reject) => {
    reader.onerror = (evt) => {
      reject(evt.target.error)
    }
    reader.onloadend = async (evt) => {
      const bytes = new Uint8Array(evt.target.result as ArrayBuffer)
      const result = await fileTypeFromBuffer(bytes)
      const mimeType = result?.mime ?? 'application/octet-stream'
      resolve(mimeType)
    }
    reader.readAsArrayBuffer(headerBlob)
  })
}

export function getFileType(mimeType: string): RemoteFile['type'] {
  if (mimeType.match(/^application\/pdf/)) {
    return 'pdf'
  } else if (mimeType.match(/^image/)) {
    return 'image'
  } else {
    return 'generic'
  }
}

export interface ImageOptimizationContext {
  detectedMimeType?: string
  entityId?: string
  fileId?: string
}

export type Size = {
  width: number
  height: number
}

export async function resizeImageFile(file: File, maxShortEdge: number, logCtx?: ImageOptimizationContext): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        console.warn(`Could not getContext('2d') for file ${file.name}`)
        Logger.logEvent('[image optimization] resize ctx null', {
          fileType: file.type,
          fileSize: file.size,
          imageWidth: img.width,
          imageHeight: img.height,
        })
        resolve(file)
        return
      }

      const shortEdge = Math.min(img.width, img.height)
      const scale = shortEdge > maxShortEdge ? maxShortEdge / shortEdge : 1
      const width = Math.round(img.width * scale)
      const height = Math.round(img.height * scale)

      canvas.width = width
      canvas.height = height
      ctx.drawImage(img, 0, 0, width, height)

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], file.name, { type: file.type }))
        } else {
          console.warn(`canvas.toBlob failed for file ${file.name}`)
          Logger.logEvent('[image optimization] resize blob null', {
            fileType: file.type,
            fileSize: file.size,
            imageWidth: img.width,
            imageHeight: img.height,
            canvasWidth: width,
            canvasHeight: height,
          })
          resolve(file)
        }
      }, file.type)

      URL.revokeObjectURL(img.src)
    }
    // fallback to original file
    img.onerror = (ev) => {
      console.warn(`img load failed for file ${file.name}`)
      Logger.logEvent('[image optimization] resize load failed', {
        fileType: file.type,
        fileExtension: getFileExtension(file.name),
        fileSize: file.size,
        ...logCtx,
      })
      URL.revokeObjectURL(img.src)
      resolve(file)
    }
    img.src = URL.createObjectURL(file)
  })
}

export function constrainDimensions(
  originalWidth: number,
  originalHeight: number,
  maxSize: Partial<Size> = {}
) {
  const { width: maxWidth, height: maxHeight } = maxSize

  if (_.isNil(maxWidth) && _.isNil(maxHeight)) {
    return { width: originalWidth, height: originalHeight }
  }

  const aspectRatio = originalWidth / originalHeight
  let newWidth = originalWidth
  let newHeight = originalHeight

  if (maxWidth && newWidth > maxWidth) {
    newWidth = maxWidth
    newHeight = newWidth / aspectRatio
  }

  if (maxHeight && newHeight > maxHeight) {
    newHeight = maxHeight
    newWidth = newHeight * aspectRatio
  }

  return {
    width: Math.round(newWidth),
    height: Math.round(newHeight),
  }
}
